import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Boxes, KeyRound, LifeBuoy, Mail, Radar, ShieldCheck } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '@/auth/AuthProvider';
import { ApiError } from '@/api/client';
import { identityApi } from '@/api/endpoints';
import { Button } from '@/design-system/Button';
import { TextInput } from '@/design-system/Inputs';
import { ThemeToggle } from './ThemeToggle';

/**
 * Sign-in.
 *
 * Production sign-in verifies the account password before emailing a one-time code. Development
 * builds also expose the local subject issuer and token entry for debugging.
 *
 * The development path calls a real endpoint that mints a real RS256 token and refuses to exist in
 * production. It is not a bypass: the token is validated exactly like any other.
 *
 * Composition: a brand panel on the left, the form on the right. The left panel exists because
 * this is the first screen anybody sees, and a lone card centred on an empty page says nothing
 * about what the product is. What it says is deliberately factual - the three things the platform
 * actually does - rather than marketing copy the software would have to live up to.
 */
/** The other ways in exist for developers and for debugging; they are not the client's path. */
const OTHER_METHODS_AVAILABLE = import.meta.env.DEV;

export function SignInPage({ onPasswordReset }: { onPasswordReset?: () => void }) {
  const { signIn, signInWithDevSubject, signOut, status, error } = useAuth();
  const [subject, setSubject] = useState('dev-admin');
  const [token, setToken] = useState('');
  const [mode, setMode] = useState<'otp' | 'dev' | 'token'>('otp');

  // The code stays beside the address that requested it, while the password remains in component state.
  const [step, setStep] = useState<'email' | 'code' | 'recovery'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordToken, setPasswordToken] = useState('');
  const [recoveryRequested, setRecoveryRequested] = useState(false);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  // Taken from the server rather than written here, so the screen cannot claim a window the
  // deployment does not actually give.
  const [validityMinutes, setValidityMinutes] = useState<number | null>(null);

  useEffect(() => {
    const consumePasswordToken = () => {
      const match = window.location.hash.match(/^#password-token=(.*)$/);
      if (!match) return;
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      setPasswordToken('');
      setPassword('');
      setPasswordConfirmation('');
      setCode('');
      setOtpError(null);
      setRecoveryRequested(false);
      setStep('email');
      try {
        const decodedToken = decodeURIComponent(match[1] ?? '');
        if (!decodedToken) throw new Error('Empty password token');
        setPasswordToken(decodedToken);
      } catch {
        setOtpError('This password link is invalid. Request a new link and try again.');
      }
    };

    consumePasswordToken();
    window.addEventListener('hashchange', consumePasswordToken);
    return () => window.removeEventListener('hashchange', consumePasswordToken);
  }, []);

  const busy = status === 'authenticating' || sending;

  const requestCode = useCallback(async () => {
    setOtpError(null);
    setCode('');
    setValidityMinutes(null);
    setSending(true);
    try {
      const sent = await identityApi.requestSignInCode(email.trim(), password);
      setValidityMinutes(Math.max(1, Math.round(sent.validitySeconds / 60)));
      setStep('code');
    } catch (caught) {
      setOtpError(
        caught instanceof ApiError && caught.status !== 401
          ? caught.message
          : 'Your email or password is incorrect.',
      );
    } finally {
      setSending(false);
    }
  }, [email, password]);

  const requestPasswordLink = async () => {
    setOtpError(null);
    setCode('');
    setRecoveryRequested(false);
    setSending(true);
    try {
      await identityApi.requestPasswordLink(email.trim());
      setRecoveryRequested(true);
    } catch (caught) {
      setOtpError(caught instanceof ApiError ? caught.message : 'The request could not be completed. Try again.');
    } finally {
      setSending(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      if (mode === 'dev') {
        await signInWithDevSubject(subject.trim());
        return;
      }
      if (mode === 'token') {
        await signIn(token.trim());
        return;
      }
      if (step === 'email') {
        await requestCode();
        return;
      }
      if (step === 'recovery') {
        await requestPasswordLink();
        return;
      }
      setOtpError(null);
      setSending(true);
      try {
        const issued = await identityApi.verifySignInCode(email.trim(), password, code.trim());
        await signIn(issued.token);
      } catch (caught) {
        setOtpError(
          caught instanceof ApiError
            ? caught.message
            : 'That code could not be checked. Try again.',
        );
      } finally {
        setSending(false);
      }
    } catch {
      // The provider records the message; nothing further to do here.
    }
  };

  const submitNewPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setOtpError(null);
    if (password !== passwordConfirmation) {
      setOtpError('The passwords do not match.');
      return;
    }
    setSending(true);
    try {
      await identityApi.completePassword(passwordToken, password);
      signOut();
      onPasswordReset?.();
      setPasswordToken('');
      setPassword('');
      setPasswordConfirmation('');
      setStep('email');
      setOtpError(null);
      setRecoveryRequested(false);
    } catch (caught) {
      setOtpError(caught instanceof ApiError ? caught.message : 'The password could not be saved. Request a new link and try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.1fr_minmax(0,480px)]">
      {/* Brand side. Hidden on small screens, where it would push the form below the fold. */}
      <aside className="signin-ground relative hidden flex-col justify-between overflow-hidden p-10 lg:flex">
        {/*
         * A drawn backdrop rather than an illustration file: three soft orbs and a faint grid.
         * It ships as nothing - no asset to load, no licence to track - and it stays crisp at any
         * size. Deliberately behind the content and low contrast; it is a ground, not a picture.
         */}
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
          <div className="signin-orb-brand absolute -top-32 -left-24 size-[28rem] rounded-full blur-3xl" />
          <div className="signin-orb-violet absolute -right-32 bottom-0 size-[26rem] rounded-full blur-3xl" />
          <div className="signin-orb-teal absolute top-1/3 right-1/4 size-[20rem] rounded-full blur-3xl" />
          <svg className="absolute inset-0 size-full opacity-[0.35]" aria-hidden>
            <defs>
              <pattern id="signin-grid" width="34" height="34" patternUnits="userSpaceOnUse">
                <path
                  d="M34 0H0v34"
                  fill="none"
                  stroke="var(--border)"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#signin-grid)" />
          </svg>
        </div>

        <Logo className="h-12" />

        <div className="max-w-md">
          <h1 className="text-display text-text-primary">
            Every device, every ticket, one record.
          </h1>
          <p className="mt-3 text-body text-text-secondary">
            The system of record for physical IT assets — what you own, who holds it, and the
            evidence that each handover actually happened.
          </p>

          <ul className="mt-8 space-y-3">
            {[
              {
                icon: Boxes,
                title: 'Asset lifecycle',
                body: 'Purchase to retirement, with every transition recorded and enforced.',
              },
              {
                icon: LifeBuoy,
                title: 'IT service desk',
                body: 'Incidents and requests tied to the device, its holder and its warranty.',
              },
              {
                icon: Radar,
                title: 'Discovery and telemetry',
                body: 'What is actually on the network, and what the register cannot account for.',
              },
            ].map((item) => (
              <li key={item.title} className="flex items-start gap-3">
                <span
                  aria-hidden
                  className="accent-blue accent-chip accent-edge accent-text mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset [&>svg]:size-4"
                >
                  <item.icon />
                </span>
                <span className="min-w-0">
                  <span className="block text-h3 text-text-primary">{item.title}</span>
                  <span className="block text-small text-text-secondary">{item.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="flex items-center gap-2 text-caption text-text-muted">
          <ShieldCheck aria-hidden className="size-3.5" />
          Tamper-evident audit log · role-based access · every action attributed
        </p>
      </aside>

      {/* Form side. */}
      <main className="relative flex items-center justify-center px-4 py-10 lg:bg-surface">
        {/* So the theme can be chosen before signing in - a demo usually starts on this screen. */}
        <ThemeToggle className="absolute top-4 right-4" />

        <div className="w-full max-w-sm">
          {/* The mark repeats here for small screens, where the brand panel is not rendered. */}
          <div className="mb-8 text-center lg:hidden">
            <h1>
              <Logo className="mx-auto h-11" />
            </h1>
          </div>

          <div className="mb-6 hidden lg:block">
            <h2 className="text-h1 text-text-primary">Sign in</h2>
            <p className="mt-1 text-small text-text-muted">
              Use your organisation account to continue.
            </p>
          </div>

          <form
            onSubmit={passwordToken ? submitNewPassword : submit}
            className="space-y-4 rounded-xl border border-border-subtle bg-surface p-6 shadow-sm lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none"
          >
            <div
              role="tablist"
              aria-label="Sign-in method"
              hidden={!OTHER_METHODS_AVAILABLE}
              className="grid grid-cols-3 gap-1 rounded-lg bg-surface-sunken p-1 data-hidden:hidden"
            >
              {(
                [
                  ['otp', 'Email code'],
                  ['dev', 'Development'],
                  ['token', 'Access token'],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  role="tab"
                  type="button"
                  aria-selected={mode === id}
                  onClick={() => setMode(id)}
                  className={
                    mode === id
                      ? 'rounded-md bg-surface px-3 py-1.5 text-small font-medium text-text-primary shadow-xs'
                      : 'rounded-md px-3 py-1.5 text-small text-text-muted hover:text-text-secondary'
                  }
                >
                  {label}
                </button>
              ))}
            </div>

            {passwordToken ? (
              <>
                <h2 className="text-h2 text-text-primary">Set up or reset your password</h2>
                <TextInput
                  label="New password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                />
                <TextInput
                  label="Confirm new password"
                  type="password"
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                />
              </>
            ) : mode === 'otp' ? (
              step === 'email' ? (
                <>
                  <TextInput
                    label="Work email"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                      setCode('');
                      setOtpError(null);
                    }}
                    placeholder="you@company.com"
                    autoComplete="email"
                    autoFocus
                    required
                  />
                  <TextInput
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setCode('');
                      setOtpError(null);
                    }}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setOtpError(null);
                      setRecoveryRequested(false);
                      setPassword('');
                      setStep('recovery');
                    }}
                    className="text-caption text-text-muted hover:text-text-secondary"
                  >
                    Set up or reset password
                  </button>
                </>
              ) : step === 'recovery' ? (
                <>
                  <h2 className="text-h2 text-text-primary">Set up or reset your password</h2>
                  {recoveryRequested ? (
                    <p role="status" className="text-small text-text-secondary">
                      If this is a provisioned account, you will receive an email with a link to set up or reset your password.
                    </p>
                  ) : (
                    <TextInput
                      label="Work email"
                      type="email"
                      value={email}
                      onChange={(event) => {
                        setEmail(event.target.value);
                        setOtpError(null);
                      }}
                      placeholder="you@company.com"
                      autoComplete="email"
                      autoFocus
                      required
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setOtpError(null);
                      setRecoveryRequested(false);
                    }}
                    className="inline-flex items-center gap-1 text-caption text-text-muted hover:text-text-secondary"
                  >
                    <ArrowLeft aria-hidden className="size-3.5" />
                    Back to sign in
                  </button>
                </>
              ) : (
                <>
                  <p className="text-small text-text-secondary">
                    {/* This step only appears after a successful code request. */}
                    {validityMinutes === null ? 'Enter the six-digit code sent to ' : 'We sent a six-digit code to '}
                    <strong className="text-text-primary">{email}</strong>.{' '}
                    {validityMinutes === null
                      ? 'It can be used once.'
                      : `It expires in ${validityMinutes} minutes and can be used once.`}
                  </p>
                  <TextInput
                    label="Code"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    // A numeric keypad on phones, and the browser offers the code from the email
                    // where the platform supports it.
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                  />
                  <div className="flex items-center justify-between text-caption text-text-muted">
                    <button
                      type="button"
                      onClick={() => {
                        setStep('email');
                        setCode('');
                        setPassword('');
                        setOtpError(null);
                      }}
                      className="inline-flex items-center gap-1 hover:text-text-secondary"
                    >
                      <ArrowLeft aria-hidden className="size-3.5" />
                      Use a different address
                    </button>
                    <button
                      type="button"
                      disabled={sending}
                      onClick={() => void requestCode()}
                      className="hover:text-text-secondary disabled:opacity-50"
                    >
                      Send another code
                    </button>
                  </div>
                </>
              )
            ) : mode === 'dev' ? (
              <TextInput
                label="Subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                placeholder="dev-admin"
                autoComplete="off"
                required
                description="The subject of a provisioned user. The seeded accounts are dev-admin, dev-manager, dev-technician, dev-auditor, dev-employee and dev-operator (platform)."
              />
            ) : (
              <TextInput
                label="Access token"
                type="password"
                value={token}
                onChange={(event) => setToken(event.target.value)}
                placeholder="eyJhbGciOi…"
                autoComplete="off"
                required
                description="A bearer token issued by the configured identity provider."
              />
            )}

            {(otpError ?? error) && (
              <p
                role="alert"
                className="rounded-md border border-danger-border bg-danger-subtle px-3 py-2 text-small text-danger-text"
              >
                {otpError ?? error}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={busy}
              leadingIcon={
                mode === 'otp' && step !== 'code' ? (
                  <Mail className="size-4" />
                ) : (
                  <KeyRound className="size-4" />
                )
              }
            >
              {passwordToken
                ? 'Save password'
                : mode === 'otp' && step === 'email'
                  ? 'Email me a code'
                  : mode === 'otp' && step === 'recovery'
                    ? recoveryRequested ? 'Request another link' : 'Email me a link'
                    : mode === 'otp' ? 'Sign in' : 'Sign in'}
            </Button>

            <p className="flex items-start gap-2 text-caption text-text-muted">
              <ShieldCheck aria-hidden className="mt-0.5 size-3.5 shrink-0" />
              Your session stays with this tab: it survives a refresh and ends when the tab is closed.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}
